/**
 * widgetComponentRegistry.ts
 * Component-layer registry mapping BannerWidgetId → React component.
 *
 * This lives in the component layer (not constants/) because it imports
 * React component modules. The constants layer (constants/bannerWidget.ts)
 * holds pure metadata only (id, label, icon, defaultEnabled).
 *
 * Together, the metadata registry and this component registry satisfy the
 * "registry-based widget system" requirement — future widget types are
 * added by appending entries to both registries without modifying
 * BannerBar rendering infrastructure.
 *
 * @see constants/bannerWidget.ts — metadata registry (pure data)
 * @see types/shared/BannerWidget.ts — BannerWidgetComponentProps contract
 */

import type { BannerWidgetId, BannerWidgetComponentProps } from '@/types/shared/BannerWidget';

import { TimeOfDayClock } from './TimeOfDayClock';
import { DailyObjectivePlaceholder } from './DailyObjectivePlaceholder';
import { EconomicEventsPlaceholder } from './EconomicEventsPlaceholder';

/**
 * Maps each banner widget ID to its rendering component.
 * `satisfies` ensures compile-time exhaustiveness — a missing widget ID is a type error.
 */
export const WIDGET_COMPONENT_MAP = {
  'clock': TimeOfDayClock,
  'daily-objective': DailyObjectivePlaceholder,
  'economic-events': EconomicEventsPlaceholder,
} as const satisfies Readonly<Record<BannerWidgetId, React.ComponentType<BannerWidgetComponentProps>>>;
